// Client wrapper over the parse-recipe-text edge function. Takes a
// freeform recipe text blob the user pasted (Notes app, website,
// email — anything) and returns a parsed structured recipe ready to
// review and save.
//
// One Haiku call edge-side; ~700ms typical. Caller handles the
// loading state + the review screen + the eventual saveRecipe.
//
// Canonical binding: we ship the canonical roster to the edge
// function so Claude binds ingredient text → canonical_id at parse
// time. Mirrors generate-recipe and identify-meal-ingredients —
// every import path now produces ingredients with structured
// canonicals, and the prompt cache on the edge side amortizes the
// roster cost across calls.
//
// Client-side stamping still runs as a safety net: if Claude leaves
// an ingredient unbound (no ingredientId, no proposedCanonical), we
// run the same inferCanonicalFromName + fuzzy chain locally so
// downstream nutrition / pairing don't drop on Claude-side misses.

import { supabase } from "./supabase";
import { inferCanonicalFromName } from "../data/ingredients";
import { resolveNameToCanonicalId } from "./recipePairing";
import { buildCanonicalRoster } from "./canonicalRoster";

/**
 * @param {object} args
 * @param {string} args.text — the pasted recipe blob (≥30 chars)
 * @returns {Promise<{ recipe: object, proposedCanonicals: Array<{slug, name, fallbackId}>|null }>}
 *   recipe — parsed recipe in user_recipes shape
 *   proposedCanonicals — synthetics minted via Path C (already
 *     upserted into pending_ingredient_info edge-side); null when
 *     Claude bound everything via Path A/B
 * @throws when the edge fn errors or returns a malformed payload
 */
export async function parseRecipeText({ text } = {}) {
  if (typeof text !== "string" || text.trim().length < 30) {
    throw new Error("paste at least a few sentences of a recipe");
  }
  const canonicalRoster = buildCanonicalRoster();
  const { data, error } = await supabase.functions.invoke("parse-recipe-text", {
    body: { text: text.trim(), canonicalRoster },
  });
  if (error) {
    let detail = "";
    const ctx = error.context;
    if (ctx && typeof ctx.text === "function") {
      try { detail = await ctx.text(); } catch { /* swallow */ }
    }
    let parsedDetail = "";
    if (detail) {
      try {
        const j = JSON.parse(detail);
        parsedDetail = j?.detail || j?.error || detail;
      } catch { parsedDetail = detail; }
    }
    throw new Error(
      parsedDetail
        ? `Couldn't parse the recipe: ${String(parsedDetail).slice(0, 300)}`
        : `Couldn't parse the recipe: ${error.message || "unknown error"}`,
    );
  }
  if (!data?.recipe || typeof data.recipe !== "object") {
    throw new Error("Couldn't parse the recipe: empty response");
  }
  const recipe = data.recipe;
  const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
  recipe.ingredients = ingredients.map((ing) => {
    if (ing?.ingredientId) return ing;
    const name = ing?.item || ing?.name || "";
    const canonicalId = inferCanonicalFromName(name) || resolveNameToCanonicalId(name) || null;
    return canonicalId ? { ...ing, ingredientId: canonicalId } : ing;
  });
  return {
    recipe,
    proposedCanonicals: Array.isArray(data.proposedCanonicals) ? data.proposedCanonicals : null,
  };
}
